import type { ModalProps } from '@workramp/ui';
import type { DateValue, ZonedDateTime } from '@internationalized/date';
import { useEffect, useState } from 'react';
import {
  Body,
  Button,
  Col,
  DatePicker,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Option,
  OptionGroup,
  Row,
  Select,
  Stack,
  Tooltip,
  useToast,
} from '@workramp/ui';
import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  getLocalTimeZone,
  parseAbsolute,
  startOfMonth,
  startOfWeek,
  startOfYear,
  today,
} from '@internationalized/date';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { BaseThemeButton } from '~/clc/features/theming/learner/components';
import { hideLoader, showLoader } from '~/common';

interface MyTeamsReportModalProps extends Omit<ModalProps, 'children'> {
  onClose: () => void;
  onGenerate: ({ fromDate, toDate }: { fromDate: DateValue | null; toDate: DateValue | null }) => void;
  academy: any;
  isOpen: boolean;
}

interface DateRange {
  fromDate: DateValue | null;
  toDate: DateValue | null;
}

type DateRangeDisplay = {
  start: ZonedDateTime | null;
  end: ZonedDateTime | null;
};

const DATE_RANGE_TYPES_OPTIONS = {
  today: 'today',
  yesterday: 'yesterday',
  custom: 'custom',
  lastWeek: 'lastWeek',
  lastMonth: 'lastMonth',
  lastYear: 'lastYear',
  thisMonth: 'thisMonth',
  thisWeek: 'thisWeek',
  thisYear: 'thisYear',
};

const CANCEL = I18n.t('js.common.modal.cancel');
const CHOOSE_A_TIME_PERIOD = I18n.t('js.academy.navigation.my_team_reports_modal.choose_a_time_period');
const CUSTOM_DATE_RANGE = I18n.t('js.academy.navigation.my_team_reports_modal.custom_date_range');
const DATE_RANGE = I18n.t('js.academy.navigation.my_team_reports_modal.date_range');
const FROM_DATE = I18n.t('js.academy.navigation.my_team_reports_modal.from_date');
const GENERATE_REPORT = I18n.t('js.academy.navigation.my_team_reports_modal.generate_report');

const TODAY = I18n.t('js.academy.navigation.my_team_reports_modal.date_range_options.today');
const YESTERDAY = I18n.t('js.academy.navigation.my_team_reports_modal.date_range_options.yesterday');
const LAST_MONTH = I18n.t('js.academy.navigation.my_team_reports_modal.date_range_options.last_month');
const LAST_WEEK = I18n.t('js.academy.navigation.my_team_reports_modal.date_range_options.last_week');
const LAST_YEAR = I18n.t('js.academy.navigation.my_team_reports_modal.date_range_options.last_year');
const THIS_MONTH = I18n.t('js.academy.navigation.my_team_reports_modal.date_range_options.this_month');
const THIS_WEEK = I18n.t('js.academy.navigation.my_team_reports_modal.date_range_options.this_week');
const THIS_YEAR = I18n.t('js.academy.navigation.my_team_reports_modal.date_range_options.this_year');

const PREDEFINED_TIME_PERIODS_GROUP_1 = I18n.t(
  'js.academy.navigation.my_team_reports_modal.predefined_time_periods.group_1',
);
const PREDEFINED_TIME_PERIODS_GROUP_2 = I18n.t(
  'js.academy.navigation.my_team_reports_modal.predefined_time_periods.group_2',
);
const PREDEFINED_TIME_PERIODS_GROUP_3 = I18n.t(
  'js.academy.navigation.my_team_reports_modal.predefined_time_periods.group_3',
);
const MY_TEAM_REPORTS_DESCRIPTION = I18n.t('js.academy.navigation.my_team_reports_modal.description');
const MY_TEAM_REPORTS_HEADER = I18n.t('js.academy.navigation.my_team_reports');
const TIME_PERIOD = I18n.t('js.academy.navigation.my_team_reports_modal.time_period');
const TO_DATE = I18n.t('js.academy.navigation.my_team_reports_modal.to_date');
const DOWNLOAD_FAILED = I18n.t('js.academy.navigation.my_team_reports_modal.download_failed');
const REPORT_DOWNLOADED_SUCCESSFULLY = I18n.t(
  'js.academy.navigation.my_team_reports_modal.report_downloaded_successfully',
);
const MAX_REPORT_SIZE_TIP = I18n.t('js.academy.navigation.my_team_reports_modal.max_report_size_tip');

export const MyTeamsReportModal = ({ academy, onClose, ...otherProps }: MyTeamsReportModalProps) => {
  const { open: openToast } = useToast();
  const theme = useTheme();

  const [dateRange, setDateRange] = useState<DateRange>({
    fromDate: null,
    toDate: null,
  });
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadErrorMsg, setDownloadErrorMsg] = useState<string>('');
  const [dateRangeType, setDateRangeType] = useState<string>(null);

  useEffect(() => {
    setDownloadErrorMsg('');
  }, [dateRange]);

  const showGenerateReportButton =
    !isDownloading && dateRange.fromDate && dateRange.toDate && dateRange.fromDate <= dateRange.toDate;

  const handleSelectChange = (value) => {
    setDateRangeType(value);
    const date = today('UTC');

    if (value === DATE_RANGE_TYPES_OPTIONS.today) {
      setDateRange({
        fromDate: date,
        toDate: date,
      });
    } else if (value === DATE_RANGE_TYPES_OPTIONS.yesterday) {
      setDateRange({
        fromDate: date.subtract({ days: 1 }),
        toDate: date.subtract({ days: 1 }),
      });
    } else if (value === DATE_RANGE_TYPES_OPTIONS.thisWeek) {
      setDateRange({
        fromDate: startOfWeek(date, 'en-US'),
        toDate: endOfWeek(date, 'en-US'),
      });
    } else if (value === DATE_RANGE_TYPES_OPTIONS.thisMonth) {
      setDateRange({
        fromDate: startOfMonth(date),
        toDate: endOfMonth(date),
      });
    } else if (value === DATE_RANGE_TYPES_OPTIONS.thisYear) {
      setDateRange({
        fromDate: startOfYear(date),
        toDate: endOfYear(date),
      });
    } else if (value === DATE_RANGE_TYPES_OPTIONS.lastWeek) {
      setDateRange({
        fromDate: startOfWeek(date.subtract({ days: 7 }), 'en-US'),
        toDate: endOfWeek(date.subtract({ days: 7 }), 'en-US'),
      });
    } else if (value === DATE_RANGE_TYPES_OPTIONS.lastMonth) {
      setDateRange({
        fromDate: startOfMonth(date.subtract({ months: 1 })),
        toDate: endOfMonth(date.subtract({ months: 1 })),
      });
    } else if (value === DATE_RANGE_TYPES_OPTIONS.lastYear) {
      setDateRange({
        fromDate: startOfYear(date.subtract({ years: 1 })),
        toDate: endOfYear(date.subtract({ years: 1 })),
      });
    } else {
      setDateRange({ fromDate: null, toDate: null });
    }
  };

  const dateRangeDisplayValue: DateRangeDisplay | null =
    dateRange?.fromDate && dateRange?.toDate
      ? {
          start: parseAbsolute(dateRange.fromDate.toDate(getLocalTimeZone()).toISOString(), 'UTC'),
          end: parseAbsolute(dateRange.toDate.toDate(getLocalTimeZone()).toISOString(), 'UTC'),
        }
      : null;

  const generateMyTeamReport = async ({ fromDate, toDate }) => {
    showLoader(false, true);
    setIsDownloading(true);

    const downloadUrl = `/academies/api/enrollments.csv?from_date=${fromDate}&to_date=${toDate}`;
    setDownloadErrorMsg(null);

    try {
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        setDownloadErrorMsg(DOWNLOAD_FAILED);
        return;
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = null;
      if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
        const [filenameMatch, filenameMatchFileName] = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatchFileName) {
          filename = filenameMatchFileName;
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      openToast({ children: REPORT_DOWNLOADED_SUCCESSFULLY });
      onClose();
    } catch (err) {
      setDownloadErrorMsg(DOWNLOAD_FAILED);
    } finally {
      hideLoader();
      setIsDownloading(false);
    }
  };

  return (
    <Modal onClose={onClose} {...otherProps}>
      <ModalHeader
        title={MY_TEAM_REPORTS_HEADER}
        description={
          <Body level="md">
            {MY_TEAM_REPORTS_DESCRIPTION}{' '}
            <Tooltip message={MAX_REPORT_SIZE_TIP} placement="right-start">
              <Icon name="info:regular" />
            </Tooltip>
          </Body>
        }
      />
      <ModalBody>
        <Stack size={4}>
          <Select
            title={TIME_PERIOD}
            label={TIME_PERIOD}
            onSelectionChange={handleSelectChange}
            placeholder={CHOOSE_A_TIME_PERIOD}
          >
            <OptionGroup aria-label={PREDEFINED_TIME_PERIODS_GROUP_1}>
              {[
                { id: DATE_RANGE_TYPES_OPTIONS.today, label: TODAY },
                { id: DATE_RANGE_TYPES_OPTIONS.yesterday, label: YESTERDAY },
              ].map((option) => (
                <Option key={option.id}>{option.label}</Option>
              ))}
            </OptionGroup>
            <OptionGroup aria-label={PREDEFINED_TIME_PERIODS_GROUP_2}>
              {[
                { id: DATE_RANGE_TYPES_OPTIONS.thisWeek, label: THIS_WEEK },
                { id: DATE_RANGE_TYPES_OPTIONS.thisMonth, label: THIS_MONTH },
                { id: DATE_RANGE_TYPES_OPTIONS.thisYear, label: THIS_YEAR },
              ].map((option) => (
                <Option key={option.id}>{option.label}</Option>
              ))}
            </OptionGroup>
            <OptionGroup aria-label={PREDEFINED_TIME_PERIODS_GROUP_3}>
              {[
                { id: DATE_RANGE_TYPES_OPTIONS.lastWeek, label: LAST_WEEK },
                { id: DATE_RANGE_TYPES_OPTIONS.lastMonth, label: LAST_MONTH },
                { id: DATE_RANGE_TYPES_OPTIONS.lastYear, label: LAST_YEAR },
              ].map((option) => (
                <Option key={option.id}>{option.label}</Option>
              ))}
            </OptionGroup>
            <OptionGroup aria-label={CUSTOM_DATE_RANGE}>
              <Option key={DATE_RANGE_TYPES_OPTIONS.custom}>{CUSTOM_DATE_RANGE}</Option>
            </OptionGroup>
          </Select>
          {dateRangeType && (
            <DatePicker
              isRange
              showDayOfWeek={false}
              forceShowYear
              label={DATE_RANGE}
              hideLabel
              startPlaceholder={FROM_DATE}
              endPlaceholder={TO_DATE}
              value={dateRangeDisplayValue}
              onChange={({ start, end }) => setDateRange({ fromDate: start, toDate: end })}
              isDateUnavailable={(val) => val.toDate(getLocalTimeZone()) > new Date()}
              isDisabled={dateRangeType !== 'custom'}
            />
          )}
          {downloadErrorMsg && (
            <Body level="md" color={theme.colors.danger[500]}>
              {downloadErrorMsg}
            </Body>
          )}
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Row justify="flex-end" gapX={2}>
          <Col xs="auto">
            <Button color="secondary" size="lg" variant="text" onPress={onClose} autoFocus isDisabled={isDownloading}>
              {CANCEL}
            </Button>
          </Col>
          <Col xs="auto">
            <BaseThemeButton
              onPress={() => generateMyTeamReport(dateRange)}
              size="lg"
              isDisabled={!showGenerateReportButton}
              themeColor={academy.themeOptions.accentColor}
            >
              {GENERATE_REPORT}
            </BaseThemeButton>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
